import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/Button';
import { useMarkRead, useNotifications, useUnreadCount } from '../../lib/api/notifications';
import { fmtRelative } from '../../lib/dates';

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { data: unreadData } = useUnreadCount();
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkRead();
  const unread = unreadData?.count ?? 0;
  const latest = notifications.slice(0, 8);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onClickNotification = (id: number, linkUrl: string, readAt: string | null) => {
    if (!readAt) markRead.mutate(id);
    setOpen(false);
    if (linkUrl) navigate(linkUrl);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <IconButton
        aria-label="Notifications"
        title={`${unread} unread`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="inbox" size={15} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 14,
              height: 14,
              background: 'var(--warn)',
              color: '#fff',
              borderRadius: 7,
              padding: '0 4px',
              fontSize: 9,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </IconButton>
      {open && (
        <div
          className="popover"
          style={{ right: 0, top: 34, minWidth: 320, maxHeight: 420, overflowY: 'auto', padding: 0 }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px 8px',
            borderBottom: '1px solid var(--border-soft)',
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>Inbox</div>
            <button
              type="button"
              className="link"
              style={{ fontSize: 11 }}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              Open all
            </button>
          </div>
          {latest.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-4)', fontSize: 12 }}>
              No notifications yet.
            </div>
          ) : (
            latest.map((n) => (
              <button
                key={n.id}
                type="button"
                className="popover-item"
                style={{
                  padding: '8px 12px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                  background: n.read_at ? 'transparent' : 'var(--bg-subtle)',
                  borderBottom: '1px solid var(--border-soft)',
                }}
                onClick={() => onClickNotification(n.id, n.link_url, n.read_at)}
              >
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{n.title}</div>
                {n.body && (
                  <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{n.body}</div>
                )}
                <div style={{ fontSize: 10.5, color: 'var(--ink-5)' }}>
                  {fmtRelative(n.created_at.slice(0, 10))}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
